/**
 * Maps a column header label (case-insensitive) to the corresponding Advance_Filter key.
 * Returns null when the column is not filterable (e.g. date, count, amount columns).
 * Ported from handleTextClick logic in the old List.js / Column_Pie.js widgets.
 *
 * @param {string} headerName - Column header label from the API response
 * @returns {string|null} Filter key, or null if the column is not filterable
 */
export function getFilterKeyFromColumnHeader(headerName) {
    if (!headerName) return null;
    const h = headerName.toLowerCase().trim();

    // Pharmacy
    if (h === 'name' || h === 'pharmacy name') return 'PharmacyName';
    if (h === 'city' || h === 'city name' || h === 'pharmacy city') return 'PhCity';
    if (h === 'state' || h === 'state name' || h === 'pharmacy state') return 'PhState';
    if (h === 'zip' || h === 'zip(*)' || h === 'pharmacy zip' || h === 'pharmacy zip(*)') return 'PhZip';

    // Prescriber
    if (h === 'prescriber' || h === 'prescriber name') return 'Prescriber';
    if (h === 'prescriber city') return 'PrCity';
    if (h === 'prescriber state') return 'PrState';
    if (h === 'prescriber zip' || h === 'prescriber zip(*)') return 'PrZip';

    // Other entities
    if (h === 'group number') return 'GroupNumber';
    if (h === 'group prefix') return 'PhGPrefix';
    if (h === 'distributor name' || h === 'distributor name/email') return 'DistributorName';
    if (h === 'drug name') return 'DrugName';
    if (h === 'wholesaler name') return 'WholesalerName';
    if (h === 'override') return 'Override';

    // PCN / BIN
    if (h === 'pcn') return 'PBM';
    if (h === 'bin') return 'PhBin';

    return null;
}

/**
 * Adds a filter tag to Advance_Filter in localStorage and triggers a dashboard refresh.
 * Ported from ProcessdashboardUpdate() in the old BarList.js / Column_Pie.js widgets.
 *
 * @param {string} nodetype  - The filter key (e.g. 'GroupNumber', 'DrugName', 'PhCity', 'PhState')
 * @param {string} nodevalue - The clicked label / value to add as a tag
 * @param {Function} onUpdate - Callback fired after localStorage is updated (triggers re-render)
 */
export function processDashboardUpdate(nodetype, nodevalue, onUpdate) {
    try {
        const rawFilter = localStorage.getItem('Advance_Filter');
        let existingFilter = null;

        if (rawFilter && rawFilter !== 'null' && rawFilter !== '{}') {
            try { existingFilter = JSON.parse(rawFilter); } catch (_) { }
        }

        if (existingFilter && typeof existingFilter === 'object' && Object.keys(existingFilter).length > 0) {
            // Filter already has entries
            let isMatched = false;

            for (const key of Object.keys(existingFilter)) {
                if (key === nodetype) {
                    isMatched = true;
                    // Only add if value isn't already present
                    const alreadyExists = existingFilter[nodetype].some(
                        v => v.toLowerCase() === nodevalue.toLowerCase()
                    );
                    if (!alreadyExists) {
                        existingFilter[nodetype].push(nodevalue);
                        const newJson = JSON.stringify(existingFilter);
                        localStorage.setItem('Advance_Filter', newJson);
                        localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
                        localStorage.setItem('Updated_AdFilter_Status', newJson);
                        if (onUpdate) onUpdate();
                    }
                    break;
                }
            }

            if (!isMatched) {
                // Add a new key to the existing filter
                existingFilter[nodetype] = [nodevalue];
                const newJson = JSON.stringify(existingFilter);
                localStorage.setItem('Advance_Filter', newJson);
                localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
                localStorage.setItem('Updated_AdFilter_Status', newJson);
                if (onUpdate) onUpdate();
            }
        } else {
            // No existing filter — create fresh
            const newFilter = { [nodetype]: [nodevalue] };
            const newJson = JSON.stringify(newFilter);
            localStorage.setItem('Advance_Filter', newJson);
            localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
            localStorage.setItem('Updated_AdFilter_Status', newJson);
            if (onUpdate) onUpdate();
        }
    } catch (error) {
        console.error('processDashboardUpdate error:', error);
    }
}
